import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  IconButton,
  Collapse,
  Chip,
  Menu,
  MenuItem,
  useTheme,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  MdAccessTime,
  MdExpandLess,
  MdExpandMore,
  MdMailOutline,
  MdOutlineLocalPhone,
  MdOutlineEdit
} from 'react-icons/md';
import { FiMoreVertical } from 'react-icons/fi';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { GoLocation, GoPlus } from 'react-icons/go';
import { IoMdTime } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { useDeleteLocation, useGetLocations } from '@/apis/business/setting/location/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import Pagination, { usePagination } from '@/components/Common/Pagination';
import LocationIcon from '@/assets/icons/LocationIcon';
import LocationsLoader from './components/Loader';
import { Location } from '@/apis/business/setting/location/types';

const LocationCard: React.FC<{ location: Location; idx: number }> = ({ location, idx }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = useState(idx === 0 ? true : false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { mutate: deleteLocation } = useDeleteLocation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

  const renderBreaks = (breaks?: { from: string; to: string }[]) => {
    if (!breaks || breaks.length === 0) {
      return (
        <Typography color='text.disabled' textAlign='center'>
          —
        </Typography>
      );
    }
    return (
      <Box display='flex' flexDirection='column' alignItems='center'>
        {breaks.map((b, index) => (
          <Typography key={index} fontSize={14} color={theme.palette.primary.main}>
            {b.from} - {b.to}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderBusinessHour = (day: string) => {
    const data = location.business_hours?.[day.toLowerCase() as keyof typeof location.business_hours];
    if (!data) return '—';
    return data.open && data.from_time && data.to_time ? `${data.from_time} - ${data.to_time}` : 'Closed';
  };

  return (
    <Card variant='outlined' sx={{ borderRadius: 2, mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack spacing={1}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='subtitle1' fontWeight={500}>
                {location.name}
              </Typography>
              <Chip
                label={location.physical_location_exists ? 'Physical' : 'Remote'}
                size='small'
                sx={{
                  bgcolor: theme.palette.primary.light,
                  color: theme.palette.primary.dark,
                  fontSize: '0.75rem',
                  borderRadius: 5
                }}
              />
            </Stack>
            <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
              <Typography variant='body2' color='secondary'>
                Contact Person -
              </Typography>
              <Typography color='black' fontWeight={400} fontSize={14}>
                {location.contact_name || 'N/A'}
              </Typography>
            </Box>
          </Stack>
          <Box>
            <IconButton onClick={handleMenuOpen}>
              <FiMoreVertical />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleMenuClose}>
                <Box display='flex' flexDirection='row' alignItems='center' onClick={() => navigate(`${location.id}`)}>
                  <MdOutlineEdit /> <Typography ml={0.5}>Edit</Typography>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
                <Box
                  display='flex'
                  flexDirection='row'
                  alignItems='center'
                  onClick={() => deleteLocation(String(location.id))}
                >
                  <RiDeleteBin6Line /> <Typography ml={0.5}>Delete</Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Stack>

        {/* Contact Info */}
        <Box display='flex' justifyContent='space-between' flexWrap='wrap' mr={10} mt={2} gap={2}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <MdMailOutline color={theme.palette.primary.main} />
            <Typography variant='body2'>{location.contact_email ?? '---'}</Typography>
          </Stack>

          <Stack direction='row' spacing={1} alignItems='center'>
            <MdOutlineLocalPhone color={theme.palette.primary.main} />
            <Typography variant='body2'>{location.contact_phone || '---'}</Typography>
          </Stack>

          <Stack direction='row' spacing={1} alignItems='center'>
            <IoMdTime color={theme.palette.primary.main} />
            <Typography variant='body2'>{location.address?.time_zone ?? '---'}</Typography>
          </Stack>

          <Stack direction='row' spacing={1} alignItems='center'>
            <GoLocation size={16} color={theme.palette.primary.main} />
            <Typography variant='body2' noWrap sx={{ maxWidth: 300 }}>
              {[
                location.address?.street_address,
                location.address?.city,
                location.address?.state,
                location.address?.postal_code,
                location.address?.country
              ]
                .filter(Boolean)
                .join(', ') || '---'}
            </Typography>
          </Stack>
        </Box>

        {/* Office Hours (New Table Layout) */}
        <Box mt={2} border='1px solid #E5E7EB' borderRadius={2} p={1.5} bgcolor='#FFFFFF' sx={{ overflow: 'hidden' }}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpen(!open)}
          >
            <Stack direction='row' alignItems='center' spacing={1}>
              <MdAccessTime color={theme.palette.primary.main} size={18} />
              <Typography variant='body2' fontWeight={500}>
                Office Hours
              </Typography>
            </Stack>
            {open ? <MdExpandLess /> : <MdExpandMore />}
          </Stack>

          <Collapse in={open}>
            <Box
              mt={1.5}
              border='1px solid #E5E7EB'
              borderRadius={2}
              overflow='auto'
              sx={{
                backgroundColor: '#FFFFFF',
                width: '100%',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#ccc',
                  borderRadius: 4
                }
              }}
            >
              <Table
                size='small'
                sx={{
                  minWidth: 700,
                  borderCollapse: 'separate',
                  borderSpacing: 0
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ borderRight: '1px solid #E5E7EB' }}> {} </TableCell>
                    {days.map((day) => (
                      <TableCell
                        key={day}
                        align='center'
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          borderRight: '1px solid #E5E7EB',
                          borderBottom: '1px solid #E5E7EB',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {day}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        borderRight: '1px solid #E5E7EB',
                        borderBottom: '1px solid #E5E7EB'
                      }}
                    >
                      SCHEDULE
                    </TableCell>
                    {days.map((day) => (
                      <TableCell
                        key={day}
                        align='center'
                        sx={{
                          color: theme.palette.primary.main,
                          borderRight: '1px solid #E5E7EB',
                          borderBottom: '1px solid #E5E7EB',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {renderBusinessHour(day)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        borderRight: '1px solid #E5E7EB'
                      }}
                    >
                      BREAKS
                    </TableCell>
                    {days.map((day) => (
                      <TableCell
                        key={day}
                        align='center'
                        sx={{
                          color: theme.palette.primary.main,
                          borderRight: '1px solid #E5E7EB',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {renderBreaks(
                          (location.business_hours?.[day.toLowerCase() as keyof typeof location.business_hours] as any)
                            ?.breaks
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  );
};

const LocationPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentPage, perPage, setPage, setPerPage } = usePagination();
  const { data: BusinessProfile } = useGetBusinessProfile({ include: 'business_plan' });
  const { data: LocationsData, isLoading } = useGetLocations({
    page: currentPage,
    per_page: perPage,
    include: 'address'
  });

  if (isLoading) return <LocationsLoader />;

  const locations = LocationsData?.data || [];
  const isLocationLimitReached = BusinessProfile?.business_plan?.included_locations < locations.length;
  const remainingLocations = BusinessProfile?.business_plan?.included_locations - locations.length;
  const additionalLocationCharge = BusinessProfile?.business_plan?.additional_per_location_charge;

  return (
    <Box>
      {BusinessProfile?.business_plan?.name && (
        <Box
          sx={{
            background: theme.palette.primary.light,
            borderRadius: 2,
            p: 2,
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <Box fontSize={22}>
              <LocationIcon />
            </Box>
            <Typography fontWeight={500} fontSize={18}>
              Location Usage
            </Typography>
            <Chip
              label={BusinessProfile?.business_plan?.name}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.dark,
                fontSize: '0.75rem',
                fontWeight: 500,
                borderRadius: 6
              }}
            />
          </Stack>
          <Box display='flex' flexDirection='column' alignItems='end'>
            <Typography color='primary' fontSize={20} fontWeight={600}>
              {locations.length}/{BusinessProfile?.business_plan?.included_locations}
            </Typography>
            <Typography color='secondary' variant='body2'>
              {isLocationLimitReached
                ? `+$${additionalLocationCharge}/additional location`
                : `${remainingLocations} location remaining`}
            </Typography>
          </Box>
        </Box>
      )}

      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2} flexWrap='wrap' gap={1}>
        <Box>
          <Typography variant='h6'>Business Locations</Typography>
          <Typography variant='body2' color='text.secondary' fontSize={16} mt={0.5}>
            Manage multiple locations for your business
          </Typography>
        </Box>
        <Button onClick={() => navigate('add')} variant='contained' color='primary' startIcon={<GoPlus />}>
          Add Locations
        </Button>
      </Stack>

      {locations.map((loc: any, idx: number) => (
        <LocationCard key={loc.id} idx={idx} location={loc} />
      ))}

      <Pagination
        totalPages={LocationsData?.meta?.total_pages}
        totalCount={LocationsData?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
    </Box>
  );
};

export default LocationPage;
